/**
 * `/replay`, and `/replay/:sessionId` — any stored session, on the phase-1
 * renderer.
 *
 * Three things a person needs and nothing else: pick a session, watch it, and
 * drag to a moment. The picker reads `manifest.json` alone, so it opens a
 * hundred sessions' worth of metadata without opening a single log.
 *
 * Picking one now writes the address bar, and an address typed in opens the
 * session directly. That is the whole handover mechanism: "watch this" is a
 * link, not a file transfer.
 *
 * The voices moved out of the metadata panel and into the transport. As bare
 * `<audio controls>` elements they were a second, unrelated player — you could
 * watch the kitchen or listen to a chef, but not both at the same moment. The
 * transport now owns them: play, pause, seek and speed drive every element
 * through `attachAudio`, anchored on the sync beep.
 *
 * There is no replay-specific renderer. The canvas here is the same
 * `startRenderer` the live round mounts, fed by the same `EventFeed`, and the
 * only difference is where the frames come from — which is the architectural
 * claim phase 1 was built around and this page is the proof of.
 *
 * What the catalogue comes from is the one thing that changed: a log that
 * carries a header carries its own, and it wins. `/api/config` is now the
 * fallback for the version-1 recordings, not the source of truth for all of
 * them — a session watched a month from now looks like the session that was
 * played, not like the recipes as they stand on the day it is watched.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TICK_MS } from '@overcooked/shared'
import type {
  AudioArtifact,
  AudioStatus,
  ClientConfig,
  SessionIndexEntry,
  SessionLog,
  SessionLogHeader,
  SessionManifest,
} from '@overcooked/shared'
import { EventFeed } from '../feed/feed'
import { prettyMapName } from '../flow/display'
import { buildCatalog } from '../render/catalog'
import { Stage } from '../render/Stage'
import { seatIdentity } from '../render/theme'
import type { ReplayPlayer } from './file'
import {
  audioUrl,
  createPlayer,
  fetchSessionIndex,
  fetchSessionLog,
  fetchSessionManifest,
  parseLog,
} from './file'
import { Conversation } from './ConversationPanel'
import type { ConversationLine } from './conversation'
import { collectConversation, conversationUpTo } from './conversation'
import type { BatchFilter, TabCondition } from './sessionTabs'
import {
  NO_BATCH,
  NO_BATCH_LABEL,
  SOLO_TAB,
  TABS,
  batchLabel,
  batchOptions,
  countByTab,
  parseConditionQuery,
  parseInviteQuery,
  rememberBatches,
  replaySearch,
  seatLabel,
  tabOf,
  visibleTabs,
} from './sessionTabs'

/** How much of a long round's conversation the panel keeps on screen. */
const CONVERSATION_LINES = 40

const clock = (ticks: number): string => {
  const seconds = Math.max(0, Math.round((ticks * TICK_MS) / 1000))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

const shortId = (id: string): string => id.slice(0, 8)

/** `1 player`, `2 players`. */
const count = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`

/** `galley-10x6` -> `Galley (galley-10x6)`: the name people use, and the id to look it up by. */
const mapLabel = (id: string): string => {
  const pretty = prettyMapName(id)
  return pretty === id ? id : `${pretty} (${id})`
}

const endedWord = (reason: string): string =>
  reason === 'timer' ? 'played to the end' : reason === 'aborted' ? 'ended early' : reason

/** An audio status in words: `ok` -> `OK`, `invalid_signature` -> `bad signature`. */
const AUDIO_WORD: Record<AudioStatus, string> = {
  ok: 'OK',
  partial: 'partial',
  invalid_signature: 'bad signature',
  upload_failed: 'upload failed',
  missing: 'missing',
}

const audioWord = (status: AudioStatus): string => AUDIO_WORD[status] ?? String(status).replace(/_/g, ' ')

/** `2026-10-12T22:59:10.161Z` -> `2026-10-12 22:59 UTC`. Anything else as it came. */
const startedWord = (iso: string): string =>
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(iso) ? `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC` : iso

/** `Red chef`, `Blue chef`: the name the kitchen and the conversation use. */
const chefName = (seat: number): string => `${seatIdentity(seat).label} chef`

const conditionName = (condition: string | null | undefined): string | null =>
  [...TABS, SOLO_TAB].find((tab) => tab.condition === condition)?.label ?? null

/**
 * The batch picker's value for "every batch". Not a code (a code has no `*`)
 * and not `''`, which is "No batch".
 */
const ALL_BATCHES = '*all*'

/**
 * `/replay`, and `/replay/<session id>`.
 *
 * Exported because `App` routes on it and this page pushes onto it, and two
 * copies of one pattern is how a link starts opening the picker instead of the
 * session it names.
 */
export const REPLAY_PATH = /^\/replay(?:\/([A-Za-z0-9_-]+))?\/?$/

const sessionIdInPath = (path: string): string | null => REPLAY_PATH.exec(path)?.[1] ?? null

function AudioRow({ artifact }: { artifact: AudioArtifact }) {
  return (
    <div className="replay-audio">
      <span className={`badge badge-${artifact.status}`}>{audioWord(artifact.status)}</span>
      <span>{chefName(artifact.seat)}</span>
      <span className="hint">
        <span className="mono">{shortId(artifact.playerId)}</span> · {(artifact.bytes / 1024).toFixed(0)} kB ·{' '}
        {count(artifact.chunkCount, 'chunk')}
      </span>
      {/* The recording itself is in the transport above, where it moves with
          the picture. What stays here is the integrity report, which is the
          only thing this panel could ever say that the transport cannot. */}
      {!artifact.key && <span className="hint">No audio recorded.</span>}
      {artifact.flags.length > 0 && (
        <ul className="replay-flags">
          {artifact.flags.map((flag, i) => (
            <li key={i}>
              <code>{flag.event}</code> {flag.detail}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function ReplayPage({ sessionId = null }: { sessionId?: string | null }) {
  // `live: false` because a replay's `ROUND_STARTED` is a fact about last
  // Tuesday. Anchoring the tick clock to it would put the origin an hour in the
  // past and run the clock to the end of the round on the first frame; the
  // transport below owns the playhead here, and the feed extrapolates between
  // frames exactly as it always did.
  const feed = useMemo(() => new EventFeed(undefined, { live: false }), [])
  const [config, setConfig] = useState<ClientConfig | null>(null)
  const [header, setHeader] = useState<SessionLogHeader | null>(null)
  const [sessions, setSessions] = useState<SessionIndexEntry[] | null>(null)
  const [manifest, setManifest] = useState<SessionManifest | null>(null)
  const [status, setStatus] = useState('Pick a round, or open a log file.')
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState({ tick: 0, playing: false })
  const playerRef = useRef<ReplayPlayer | null>(null)
  const [lastTick, setLastTick] = useState(0)
  const [openId, setOpenId] = useState<string | null>(sessionId)
  // Read by the `popstate` listener, which is registered once and would
  // otherwise see the id from the render that registered it.
  const openIdRef = useRef<string | null>(sessionId)
  openIdRef.current = openId
  // Bumped whenever a new player is built, so the effect that hands it the
  // audio elements has something to depend on. A ref alone would let the two
  // drift: a mounted player with nobody's voice attached to it.
  const [mounted, setMounted] = useState(0)
  const audioRefs = useRef(new Map<string, HTMLAudioElement>())
  // The whole conversation, read once when the log is mounted. It is filtered
  // by the playhead below rather than accumulated by the feed, because a seek
  // starts from the last keyframe and a feed-based transcript would forget
  // everything said before it.
  const [conversation, setConversation] = useState<ConversationLine[]>([])
  // Which study condition the picker shows. It lives in the query string, so a
  // link to "the AI–AI rounds" is a link, and the back button restores the tab.
  const [condition, setCondition] = useState<TabCondition>(() =>
    parseConditionQuery(window.location.search),
  )
  // Which batch the list is narrowed to, also in the query string. `undefined`
  // — no `?invite=` — is every batch, which is what a link from before batches
  // existed asks for and still gets.
  const [invite, setInvite] = useState<BatchFilter>(() => parseInviteQuery(window.location.search))
  // Every batch any loaded list has mentioned. The filtered list only names its
  // own batch, so the picker's options come from everything seen, not from the
  // rows on screen — otherwise choosing a batch would hide all the others.
  const [seenBatches, setSeenBatches] = useState<readonly string[]>([])

  // The fallback catalogue, for a version-1 log that carries none of its own.
  // It is the identical one the round page builds, which is why `/replay` shows
  // the identical kitchen, dockets and fuses — the clearest statement available
  // that the renderer's only input is the feed.
  useEffect(() => {
    let cancelled = false
    void fetch('/api/config')
      .then((response) => response.json() as Promise<ClientConfig>)
      .then((value) => {
        if (!cancelled) setConfig(value)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])
  // The log's own catalogue wins whenever the file has one. A recipe retuned
  // since the round was played must not re-skin the recording of it, and this
  // one line is the whole difference between a file that describes itself and
  // a file that borrows a description from whatever the server serves today.
  const catalog = useMemo(() => buildCatalog(header?.catalogue ?? config), [header, config])

  // Filtered on the server, so a batch is one request over the index rather
  // than every row shipped and thrown away here. Re-run when the batch changes.
  useEffect(() => {
    let cancelled = false
    fetchSessionIndex({ invite })
      .then((index) => {
        if (cancelled) return
        setSessions(index.sessions)
        setSeenBatches((known) => rememberBatches(known, index.sessions))
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setSessions([])
          setStatus(`Couldn't load the list. ${err.message}`)
        }
      })
    return () => {
      cancelled = true
    }
  }, [invite])

  useEffect(() => () => playerRef.current?.stop(), [])

  const mount = useCallback(
    (log: SessionLog, label: string) => {
      playerRef.current?.stop()
      setHeader(log.header)
      setConversation(collectConversation(log.frames, log.header))
      const player = createPlayer(log.frames, feed, {
        onProgress: (tick, playing) => setProgress({ tick, playing }),
      })
      playerRef.current = player
      setLastTick(player.index.lastTick)
      setMounted((n) => n + 1)
      // Land on tick 0 rather than a blank canvas: the first keyframe is what
      // makes the kitchen appear, and it is the first thing in every log.
      player.seek(player.index.firstTick)
      player.play()
      setStatus(
        `${label} · ${clock(player.index.lastTick)} long` +
          (log.header
            ? ` · ${prettyMapName(log.header.mapName)}`
            : ' · Old log file, so dish names come from this server.'),
      )
    },
    [feed],
  )

  const openSession = useCallback(
    async (id: string) => {
      setLoading(true)
      setManifest(null)
      setOpenId(id)
      setStatus(`Loading ${id}…`)
      try {
        // The log first: a session with no manifest is still watchable, and the
        // manifest is what a manual `rebuild` would regenerate anyway.
        mount(await fetchSessionLog(id), id)
        try {
          setManifest(await fetchSessionManifest(id))
        } catch {
          setStatus((s) => `${s} · No round summary saved, so no audio details.`)
        }
      } catch (err) {
        setStatus(`Couldn't open ${id}. ${(err as Error).message}`)
      } finally {
        setLoading(false)
      }
    },
    [mount],
  )

  /**
   * Clicking a session is also an address change.
   *
   * `pushState` rather than a reload, because the page has already fetched the
   * index and the catalogue; and it is a push rather than a replace so the back
   * button walks the sessions somebody has looked at.
   */
  const chooseSession = useCallback(
    (id: string) => {
      // The query string rides along, so going back from a session lands on
      // the tab it was picked from.
      window.history.pushState(null, '', `/replay/${encodeURIComponent(id)}${window.location.search}`)
      void openSession(id)
    },
    [openSession],
  )

  /**
   * Choosing a tab is an address change too, on whatever path is open. The
   * batch rides along, so switching tabs never drops the batch filter.
   */
  const chooseCondition = useCallback(
    (next: TabCondition) => {
      window.history.pushState(null, '', `${window.location.pathname}${replaySearch(next, invite)}`)
      setCondition(next)
    },
    [invite],
  )

  /** Choosing a batch is the same kind of address change, keeping the tab. */
  const chooseBatch = useCallback(
    (next: BatchFilter) => {
      window.history.pushState(null, '', `${window.location.pathname}${replaySearch(condition, next)}`)
      setInvite(next)
    },
    [condition],
  )

  // An address typed in, or a link somebody was sent. This is the whole reason
  // the URL carries the id: the person receiving it does nothing but open it.
  useEffect(() => {
    if (sessionId) void openSession(sessionId)
    // Deliberately once, on the id the page was opened with: re-running on
    // `openSession` would restart the session every time the picker re-rendered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  // The back button walks the sessions somebody looked at, which is only true
  // if something listens: `pushState` alone changes the address and nothing else.
  useEffect(() => {
    const onPop = (): void => {
      setCondition(parseConditionQuery(window.location.search))
      setInvite(parseInviteQuery(window.location.search))
      const id = sessionIdInPath(window.location.pathname)
      // Only a changed id reopens: stepping back across a tab change must not
      // restart the session that is already playing.
      if (id && id === openIdRef.current) return
      if (id) void openSession(id)
      else {
        playerRef.current?.pause()
        setOpenId(null)
      }
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [openSession])

  const onFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return
      setManifest(null)
      setOpenId(null)
      try {
        mount(parseLog(await file.text()), file.name)
      } catch (err) {
        setStatus(`Couldn't read that file. ${(err as Error).message}`)
      }
    },
    [mount],
  )

  const player = playerRef.current

  const counts = useMemo(() => countByTab(sessions ?? []), [sessions])
  const visible = useMemo(
    () => (sessions ?? []).filter((session) => tabOf(session) === condition),
    [sessions, condition],
  )
  // Unlabelled joins the strip only when an old session could not be classified.
  const tabs = useMemo(() => visibleTabs(counts, condition), [counts, condition])
  const conditionLabel = tabs.find((tab) => tab.condition === condition)?.label ?? condition
  const batches = useMemo(() => batchOptions(seenBatches.map((code) => ({ invite: code })), invite), [
    seenBatches,
    invite,
  ])
  const batchName =
    invite === undefined ? null : invite === NO_BATCH ? `"${NO_BATCH_LABEL}"` : `batch ${invite}`
  // Shown once there is anything to choose between, and always while a filter
  // is on — a picker that vanished with its own empty result could not be undone.
  const showBatchPicker = sessions !== null && (invite !== undefined || batches.length > 0)

  // Where the voices live, and where their clock starts. The manifest wins when
  // there is one — it is written after the uploads and is the only thing that
  // can say whether they landed — and the header stands in for a dropped file.
  const audioSessionId = manifest?.sessionId ?? header?.sessionId ?? openId
  const syncBeepTick = manifest?.syncBeepTick ?? header?.syncBeepTick ?? null
  // Memoised on the two things it is derived from, because the attach effect
  // keys on its identity: rebuilt every render, it would re-attach every render.
  const voices = useMemo(
    () => (manifest?.audio ?? header?.audio ?? []).filter((artifact) => artifact.key !== null),
    [manifest, header],
  )

  // Everything said and done at or before the playhead. Recomputed as the tick
  // moves, which is what makes a line appear on the tick it was said and makes
  // dragging the scrub bar backwards take the conversation back with it.
  const heard = useMemo(
    () => conversationUpTo(conversation, progress.tick, CONVERSATION_LINES),
    [conversation, progress.tick],
  )

  const holdAudio = useCallback((playerId: string, element: HTMLAudioElement | null) => {
    if (element) audioRefs.current.set(playerId, element)
    else audioRefs.current.delete(playerId)
  }, [])

  /**
   * Hand the mounted elements to the transport.
   *
   * It runs after the commit that created them, which is why the refs are read
   * here rather than when the session was opened: at that moment the manifest
   * had not arrived and there was nothing to attach.
   */
  useEffect(() => {
    const active = voices
      .map((artifact) => audioRefs.current.get(artifact.playerId))
      .filter((element): element is HTMLAudioElement => Boolean(element))
    playerRef.current?.attachAudio(active, syncBeepTick)
  }, [voices, syncBeepTick, mounted])

  return (
    <main className="page">
      <header className="bar">
        <span className="brand">
          Over<em>cooked</em>
        </span>
        <span className="code">Replay</span>
        {/* A styled label rather than a bare file input: this page is shown to
            whoever evaluates the work, and a default OS button is a tell. */}
        <label className="file-pick">
          Open a log file
          <input type="file" accept=".jsonl,.json,.txt" onChange={onFile} />
        </label>
      </header>

      <div className="replay-layout">
      <section className="replay-picker">
        <h2>Recorded rounds</h2>
        {sessions === null && <p className="hint">Loading rounds…</p>}
        {showBatchPicker && (
          <label className="batch-filter">
            Batch
            <select
              value={invite ?? ALL_BATCHES}
              onChange={(event) =>
                chooseBatch(event.target.value === ALL_BATCHES ? undefined : event.target.value)
              }
            >
              <option value={ALL_BATCHES}>All batches</option>
              <option value={NO_BATCH}>{NO_BATCH_LABEL}</option>
              {batches.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </label>
        )}
        {sessions?.length === 0 && batchName === null && (
          <p className="hint">No rounds yet. Play one, or open a log file.</p>
        )}
        {sessions?.length === 0 && batchName !== null && (
          <p className="hint">No rounds in {batchName} yet.</p>
        )}
        {sessions && sessions.length > 0 && (
          <div className="replay-tabs" role="tablist" aria-label="Study condition">
            {tabs.map((tab) => (
              <button
                key={tab.condition}
                type="button"
                role="tab"
                aria-selected={condition === tab.condition}
                onClick={() => chooseCondition(tab.condition)}
              >
                {tab.label} <span className="count">{counts[tab.condition]}</span>
              </button>
            ))}
          </div>
        )}
        {sessions && sessions.length > 0 && visible.length === 0 && (
          <p className="hint">
            No {conditionLabel} rounds{batchName ? ` in ${batchName}` : ''} yet.
          </p>
        )}
        {visible.length > 0 && (
          <ul className="replay-list" role="tabpanel">
            {visible.map((session) => (
              <li key={session.sessionId}>
                <button
                  type="button"
                  disabled={loading}
                  className={openId === session.sessionId ? 'chosen' : undefined}
                  onClick={() => chooseSession(session.sessionId)}
                >
                  <span className="mono">{session.sessionId}</span>
                  <span className="hint">
                    {clock(session.lastTick)} · score {session.score} · {count(session.playerCount, 'player')} ·{' '}
                    {endedWord(session.reason)}
                    {session.audioStatus.length > 0 && ` · audio ${session.audioStatus.map(audioWord).join(', ')}`}
                  </span>
                  <span className="hint replay-batch-label">{batchLabel(session)}</span>
                  {session.seats && session.seats.length > 0 && (
                    <span className="hint replay-seats">{session.seats.map(seatLabel).join(' · ')}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="replay-stage">
      <div className="replay-transport">
        <button
          type="button"
          disabled={!player}
          onClick={() => (progress.playing ? player?.pause() : player?.play())}
        >
          {progress.playing ? 'Pause' : 'Play'}
        </button>
        <input
          type="range"
          min={0}
          max={Math.max(1, lastTick)}
          value={Math.round(progress.tick)}
          disabled={!player}
          // Dragging seeks straight away rather than on release: the whole
          // point of the keyframes is that a seek costs a fold of at most two
          // seconds, so it can happen on every pointer move.
          onChange={(event) => player?.seek(Number(event.target.value))}
        />
        <span className="mono">
          {clock(progress.tick)} / {clock(lastTick)}
        </span>
        <select
          defaultValue="1"
          disabled={!player}
          onChange={(event) => player?.setSpeed(Number(event.target.value))}
        >
          <option value="0.5">0.5x</option>
          <option value="1">1x</option>
          <option value="2">2x</option>
          <option value="4">4x</option>
        </select>
      </div>

      {/* The voices, driven rather than offered. No `controls`: a second set of
          play buttons is exactly the thing that let the sound and the picture
          disagree. The elements are here, inside the transport, because that is
          what they now belong to. */}
      {audioSessionId && voices.length > 0 && (
        <div className="replay-voices">
          <span className="hint">Voices</span>
          {voices.map((artifact) => (
            <span className="replay-voice" key={artifact.playerId}>
              <span className="mono">{seatIdentity(artifact.seat).label}</span>
              <span className={`badge badge-${artifact.status}`}>{audioWord(artifact.status)}</span>
              <audio
                preload="auto"
                ref={(element) => holdAudio(artifact.playerId, element)}
                src={audioUrl(audioSessionId, artifact.playerId)}
              />
            </span>
          ))}
          <span className="hint">Audio follows the timeline.</span>
        </div>
      )}

      {/* What the AI chefs said and did, in the slot the voices use: text
          instead of audio. Gated on the tape having any, not on the playhead,
          so the panel does not pop in mid-round — and a human-human log, which
          carries no PLAN or UTTERANCE, never renders it at all. */}
      {conversation.length > 0 && <Conversation lines={heard} header={header} />}

      <Stage feed={feed} catalog={catalog} selfId={null} className="stage-replay" />

      <p className="hint">{status}</p>

      </section>
      </div>

      {/* A dropped file has no manifest — but from version 2 on it has a header,
          which carries the same first sentence. Audio stays the manifest's to
          report: it is written after the uploads, and is the only one that can
          say whether they landed. */}
      {!manifest && header && (
        <section className="replay-manifest">
          <h2>{header.sessionId}</h2>
          <p className="hint">
            {mapLabel(header.mapName)} · {startedWord(header.startedAt)} · {count(header.players.length, 'player')} ·
            sync beep at tick {header.syncBeepTick ?? 'none'}
            {/* Version 3 onward. A version-2 file has no answer to this, which
                is a fact about the file rather than a gap to paper over. */}
            {header.condition ? ` · ${conditionName(header.condition) ?? header.condition}` : ''}
            {/* Version 4 onward says its batch; older files are no batch. */}
            {` · ${batchLabel(header)}`}
            {header.participants?.some((p) => p.kind === 'agent')
              ? ` · ${header.participants
                  .filter((p) => p.kind === 'agent')
                  .map((p) => `${chefName(p.seat)}: ${p.model ?? 'AI chef'}`)
                  .join(', ')}`
              : ''}
          </p>
        </section>
      )}

      {manifest && (
        <section className="replay-manifest">
          <h2>{manifest.sessionId}</h2>
          <p className="hint">
            {mapLabel(manifest.mapName)} · {startedWord(manifest.startedAt)} · {endedWord(manifest.reason)} ·
            score {manifest.score} · sync beep at tick {manifest.syncBeepTick ?? 'none'} · {batchLabel(manifest)}
          </p>
          {manifest.audio.map((artifact) => (
            <AudioRow key={artifact.playerId} artifact={artifact} />
          ))}
        </section>
      )}
    </main>
  )
}

export default ReplayPage
